import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { MidiNote } from '../types/midi';
import { Note } from '../types/note';

interface VirtualKeyboardContextType {
  activeNotes: MidiNote[];
  addTouchNote: (note: Note) => void;
  removeTouchNote: (note: Note) => void;
  toggleClickNote: (note: Note) => void;
  clearClickNotes: () => void;
}

const VirtualKeyboardContext = createContext<VirtualKeyboardContextType | undefined>(undefined);

export const VirtualKeyboardProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [activeNotes, setActiveNotes] = useState<MidiNote[]>([]);

  const addTouchNote = useCallback((note: Note) => {
    setActiveNotes(prevNotes => {
      // Check if the note is already active to avoid duplicates
      if (prevNotes.some(n => n.number === note.value && n.source === 'touch')) {
        return prevNotes;
      }

      // Add the note
      const newNote: MidiNote = {
        number: note.value,
        velocity: 64,  // Default velocity
        name: note.toString(true),
        octave: Math.floor(note.value / 12) - 1,
        invalidated: false,
        source: 'touch'
      };
      return [...prevNotes, newNote];
    });
  }, []);

  const removeTouchNote = useCallback((note: Note) => {
    setActiveNotes(prevNotes =>
      prevNotes.filter(n => !(n.number === note.value && n.source === 'touch'))
    );
  }, []);

  const toggleClickNote = useCallback((note: Note) => {
    setActiveNotes(prevNotes => {
      const existingIndex = prevNotes.findIndex(n => n.number === note.value && n.source === 'click');

      if (existingIndex >= 0) {
        // Remove the note if it's already active
        return prevNotes.filter(n => n.number !== note.value || n.source !== 'click');
      } else {
        // Add the note if it's not active
        const newNote: MidiNote = {
          number: note.value,
          velocity: 64,  // Default velocity
          name: note.toString(true),
          octave: Math.floor(note.value / 12) - 1,
          invalidated: false,
          source: 'click'
        };
        return [...prevNotes, newNote];
      }
    });
  }, []);

  const clearClickNotes = useCallback(() => {
    setActiveNotes(prevNotes =>
      prevNotes.filter(n => n.source !== 'click')
    );
  }, []);

  return (
    <VirtualKeyboardContext.Provider value={{
      activeNotes,
      addTouchNote,
      removeTouchNote,
      toggleClickNote,
      clearClickNotes
    }}>
      {children}
    </VirtualKeyboardContext.Provider>
  );
};

export const useVirtualKeyboard = (): VirtualKeyboardContextType => {
  const context = useContext(VirtualKeyboardContext);
  if (context === undefined) {
    throw new Error('useVirtualKeyboard must be used within a VirtualKeyboardProvider');
  }
  return context;
};
